import { useCallback, useEffect, useState } from 'react';
import {
    Box, Paper, Stack, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Skeleton, Button,
    TablePagination, Slide, IconButton, Popover, Chip, TextField,
} from '@mui/material';
import { InfoOutlined, DeleteOutline, Clear } from '@mui/icons-material';
import * as AuditWpObjectiveService from '@/services/auditService/auditWpObjectiveService';
import { AuditWpObjectiveVO } from '@/services/auditService/auditWpObjectiveService';
import type { ResponsePage } from '@/services/page.interface';
import { SnackbarField } from '@/components/common';
import WorkeDoneDetail from '@/pages/components/manage/dictionary/objectives/WorkeDoneDetail';

export default function AuditWpObjectiveList() {
    const [data, setData] = useState<ResponsePage<AuditWpObjectiveVO>>({
        list: [], totalRow: 0, pageSize: 25, pageNumber: 1, totalPage: 0, firstPage: true, lastPage: true,
    });
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailId, setDetailId] = useState<string | undefined>(undefined);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    // 搜索相关状态
    const [searchInput, setSearchInput] = useState('');      // 输入框实时值
    const [searchKeyword, setSearchKeyword] = useState('');  // 防抖后实际用于查询的关键词

    // 获取数据（支持分页 + 搜索关键词）
    const fetchData = useCallback(async (pageNumber: number, pageSize: number, content?: string) => {
        setLoading(true);
        try {
            let res;
            if (content && content.trim() !== '') {
                res = await AuditWpObjectiveService.listByContent(pageNumber, pageSize, content);
            } else {
                res = await AuditWpObjectiveService.getList(pageNumber, pageSize);
            }
            setData(res);
        } catch (e: any) {
            setSnackbar({ open: true, message: e?.message || 'Failed to load', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, []);

    // 分页或每页条数变化时触发（携带当前搜索关键词）
    useEffect(() => {
        fetchData(page + 1, rowsPerPage, searchKeyword);
    }, [page, rowsPerPage, searchKeyword, fetchData]);

    // 搜索输入防抖：用户停止输入 500ms 后更新关键词并重置页码
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchKeyword(searchInput);
            setPage(0); // 重置到第一页
        }, 500);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // 清空搜索框
    const handleClearSearch = () => {
        setSearchInput('');
        setSearchKeyword('');
        setPage(0);
    };

    const handleRemove = async (id: string) => {
        try {
            await AuditWpObjectiveService.removeById(id);
            setSnackbar({ open: true, message: 'Deleted successfully', severity: 'success' });
            // 删除后刷新当前页（保持搜索关键词）
            fetchData(page + 1, rowsPerPage, searchKeyword);
        } catch (e: any) {
            setSnackbar({ open: true, message: e?.message || 'Delete failed', severity: 'error' });
        }
    };

    const openDetail = (id?: string) => {
        setDetailId(id);
        setDialogOpen(true);
    };

    const handleCloseDetail = (refreshed?: boolean) => {
        setDialogOpen(false);
        setDetailId(undefined);
        if (refreshed) fetchData(page + 1, rowsPerPage, searchKeyword);
    };

    return (
        <Slide direction="left" in mountOnEnter unmountOnExit>
            <Box sx={{
                p: 2,
                height: 'calc(100vh - 120px)',
                minHeight: '70vh',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                {/* 头部：标题 + 搜索框 + 创建按钮 */}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1} mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700}>Work Done Dictionary</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                        <TextField
                            size="small"
                            label="Search Content"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            InputProps={{
                                endAdornment: searchInput && (
                                    <IconButton size="small" onClick={handleClearSearch} edge="end">
                                        <Clear fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                            sx={{ minWidth: 200 }}
                        />
                        <Button variant="contained" color="secondary" onClick={() => openDetail()}>
                            Create
                        </Button>
                    </Stack>
                </Stack>

                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Symbol</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Content</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: 120 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && [...Array(rowsPerPage)].map((_, i) => (
                                <TableRow key={i}>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                    <TableCell><Skeleton /></TableCell>
                                </TableRow>
                            ))}
                            {!loading && data.list.length === 0 && (
                                <TableRow><TableCell colSpan={4} align="center">No data</TableCell></TableRow>
                            )}
                            {!loading && data.list.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell>{row.symbol ? unescapeSymbol(row.symbol) : '-'}</TableCell>
                                    <TableCell>{row.content || '-'}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                            {(row.code || []).map(c => <Chip key={c} label={c} size="small" />)}
                                        </Stack>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton size="small" color="primary" onClick={() => openDetail(row.id)}>
                                                <InfoOutlined fontSize="small" />
                                            </IconButton>
                                            <DeletePopover
                                                nodeId={row.id!}
                                                nodeName={row.content || ''}
                                                onRemove={handleRemove}
                                            />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                    <TablePagination
                        component="div"
                        count={data.totalRow}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        sx={{ flexShrink: 0 }}
                    />

                <WorkeDoneDetail
                    open={dialogOpen}
                    objectiveId={detailId}
                    onClose={handleCloseDetail}
                    onSnackbar={(msg, severity) => setSnackbar({ open: true, message: msg, severity })}
                />
                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                />
            </Box>
        </Slide>
    );
}

// 辅助函数：将 Unicode 转义字符串还原为原始字符（例如 "\u2113" -> "ℓ"）
function unescapeSymbol(str: string): string {
    try {
        return JSON.parse(`"${str}"`);
    } catch {
        return str;
    }
}

// 删除确认 Popover
function DeletePopover({ nodeId, nodeName, onRemove }: { nodeId: string; nodeName: string; onRemove: (id: string) => void }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    return (
        <>
            <IconButton size="small" color="error" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <DeleteOutline fontSize="small" />
            </IconButton>
            <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
                <Box p={2}>
                    <Typography variant="body2">Confirm to delete "{nodeName}"?</Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
                        <Button size="small" onClick={() => setAnchorEl(null)}>Cancel</Button>
                        <Button size="small" color="error" variant="contained" onClick={() => { onRemove(nodeId); setAnchorEl(null); }}>
                            Confirm
                        </Button>
                    </Stack>
                </Box>
            </Popover>
        </>
    );
}